/**
 * Demo Landing Page (/demo)
 *
 * Public-facing entry point that lets prospects start a hands-on sales demo
 * with one click. Pre-loads them as a temporary admin in a shared "Demo Hospice"
 * tenant with synthetic patients, notes, audits, LCD crosswalks, and defense data.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorFormatter';
import {
  Sparkles,
  Shield,
  Clock,
  Users,
  Database,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Eye,
  Zap,
  ShieldCheck,
  FileSignature,
  Stethoscope,
  Brain,
  Lock,
  PlayCircle,
  Ban,
} from 'lucide-react';
import SEO from '../components/SEO';
import { PublicPageCopy } from '../components/PublicPageCopy';

const DemoFeature = ({ icon: Icon, title, body }) => (
  <div className="flex gap-3" data-testid={`demo-feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-emerald-400" />
    </div>
    <div>
      <p className="font-semibold text-white text-sm">{title}</p>
      <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{body}</p>
    </div>
  </div>
);

const DemoLandingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    // Already authenticated — just route into the app
    if (token) {
      navigate('/app/dashboard');
      return;
    }
    axios
      .get(`${API}/demo/status`)
      .then((res) => setStatus(res.data))
      .catch(() => {
        // non-fatal; page still works
      });
  }, [token, navigate]);

  const startDemo = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo/start`, {
        display_name: displayName || undefined,
        email_hint: emailHint || undefined,
        referral_source: 'demo-landing',
      });
      // P3 Cookie Migration: cookie is set server-side. Hard-navigate to /app
      // so AuthProvider mounts fresh and picks up the new session via /me.
      toast.success('Demo session started — synthetic data only');
      window.location.href = '/app/dashboard';
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start demo. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white relative overflow-hidden"
      data-testid="demo-landing-page"
    >
      <SEO
        title="Live Demo — HospiceGuard Hospice Audit Defense Software"
        description="Try HospiceGuard free in 60 seconds — no signup. Explore AI-powered hospice audit defense with synthetic patients, live LCD scoring, TPE/CGS audits, and sample defense packages."
        path="/demo"
        keywords="hospice audit software demo, hospice compliance software free trial"
      />
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.06)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_70%)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-3xl" />

      {/* Top nav */}
      <nav className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-logo-link">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold leading-none">HospiceGuard</p>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400">Live Demo</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white" data-testid="demo-signin-link">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-emerald-600 hover:bg-emerald-500" data-testid="demo-register-link">
                Free Trial <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Left — pitch */}
          <div className="lg:col-span-3">
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10">
              <Sparkles className="w-3 h-3 mr-1" /> Live Hands-On Sales Demo
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight mb-6">
              See HospiceGuard{' '}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                in 60 seconds
              </span>
              .
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed mb-10">
              No signup. No credit card. One click and you're inside our full audit-defense
              platform with synthetic patients, real audit results, and live AI features —
              ready to test-drive every workflow.
            </p>

            {/* Live demo features */}
            <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mb-10">
              <DemoFeature
                icon={Stethoscope}
                title="6 Synthetic Patients"
                body="Alzheimer's, ESRD, COPD, cancer, hepatic failure — designed to demo every LCD."
              />
              <DemoFeature
                icon={Brain}
                title="AI Audit Engine Live"
                body="Run TPE + CGS dual audits and watch the AI flag gaps in real time."
              />
              <DemoFeature
                icon={ShieldCheck}
                title="Pre-Built Audit Results"
                body="Mix of PASS / WARNING / FAIL outcomes to walk through the defense flow."
              />
              <DemoFeature
                icon={FileSignature}
                title="Sample Defense Package"
                body="Real ADR appeal package with LCD evidence + visit-note crosswalk."
              />
              <DemoFeature
                icon={Eye}
                title="HIPAA Proof Center"
                body="Live de-identification engine and tamper-evident audit trail demo."
              />
              <DemoFeature
                icon={Zap}
                title="60-Minute Session"
                body="Plenty of time to explore. Auto-resets so the next prospect gets a clean slate."
              />
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-emerald-500" />
                100% Synthetic Data
              </div>
              <div className="flex items-center gap-1.5">
                <Ban className="w-4 h-4 text-amber-500" />
                Live FHIR Push Disabled in Demo
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-emerald-500" />
                Resets Nightly
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                No Real PHI
              </div>
            </div>
          </div>

          {/* Right — start panel */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900/80 backdrop-blur border-emerald-500/20 shadow-2xl shadow-emerald-500/10">
              <CardContent className="pt-6 space-y-5">
                <div>
                  <h3 className="text-xl font-bold mb-1">Start Your Demo</h3>
                  <p className="text-sm text-slate-400">
                    Optional — leave blank for fully anonymous.
                  </p>
                </div>

                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Your Name (optional)</label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Jane Smith"
                    className="bg-slate-950 border-slate-700 text-white"
                    maxLength={80}
                    data-testid="demo-display-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Work Email (optional)</label>
                  <Input
                    value={emailHint}
                    onChange={(e) => setEmailHint(e.target.value)}
                    placeholder="jane@yourhospice.com"
                    className="bg-slate-950 border-slate-700 text-white"
                    maxLength={120}
                    data-testid="demo-email-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Used only to send you a follow-up trial invite if you're interested.
                  </p>
                </div>

                <Button
                  onClick={startDemo}
                  disabled={loading}
                  size="lg"
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-semibold text-base h-12 shadow-lg shadow-emerald-500/25"
                  data-testid="start-demo-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <PlayCircle className="w-5 h-5 mr-2" />
                  )}
                  Start Live Demo Now
                </Button>

                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  By starting the demo, you agree the data shown is synthetic and that activity may
                  be logged for support / quality purposes. No real PHI is involved.
                </p>

                {status && (
                  <div className="border-t border-slate-800 pt-4 mt-2 grid grid-cols-3 gap-2 text-center">
                    <div data-testid="demo-stat-patients">
                      <p className="text-2xl font-bold text-emerald-400">
                        {status.synthetic_patients}
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Patients</p>
                    </div>
                    <div data-testid="demo-stat-notes">
                      <p className="text-2xl font-bold text-emerald-400">
                        {status.visit_notes_seeded}
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Visit Notes</p>
                    </div>
                    <div data-testid="demo-stat-audits">
                      <p className="text-2xl font-bold text-emerald-400">
                        {status.audit_results_seeded}
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Audits</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 text-center mt-4">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300 underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Bottom — what to try */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <div className="border-t border-white/10 pt-12">
          <h2 className="text-2xl font-bold mb-2">Suggested 5-Minute Demo Tour</h2>
          <p className="text-slate-400 mb-8">
            We pre-built these scenarios so you can experience every flagship feature.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                num: '01',
                title: 'Run the TPE Audit',
                body: 'Open Margaret O\'Brien\'s chart → click "TPE Audit". Watch the 3-layer engine score her dementia LCD eligibility in seconds.',
              },
              {
                num: '02',
                title: 'Defend a Failed Audit',
                body: 'Open Robert Chen — he failed the F2F check. Generate a multi-level appeal with patient-specific LCD evidence in one click.',
              },
              {
                num: '03',
                title: 'See HIPAA Proof Live',
                body: 'Visit "HIPAA Proof Center" → paste any clinical text → watch all 18 HIPAA identifiers stripped in real time.',
              },
              {
                num: '04',
                title: 'Voice-Dictate a Note',
                body: 'Open AI Scribe → choose any of 10 medical accents → dictate. Whisper transcribes with medical-term accuracy.',
              },
              {
                num: '05',
                title: 'Patient-Specific LCD Crosswalk',
                body: 'Open Helen Martinez (COPD borderline). Run LCD Crosswalk to see auto-discrepancy detection + addendum suggestions.',
              },
              {
                num: '06',
                title: 'Try the Compliance Coach',
                body: 'Ask the AI Compliance Coach any question — it cites real Medicare references inline.',
              },
            ].map((step) => (
              <Card
                key={step.num}
                className="bg-slate-900/60 border-slate-800 hover:border-emerald-500/40 transition-colors"
                data-testid={`demo-tour-step-${step.num}`}
              >
                <CardContent className="pt-6">
                  <p className="text-xs font-mono text-emerald-400 mb-2">{step.num}</p>
                  <h4 className="font-semibold mb-2">{step.title}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{step.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <div className="relative z-10"><PublicPageCopy page="demo" tone="dark" /></div>

      <footer className="relative z-10 border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2">
          <p>© 2026 HospiceGuard — Demo Environment</p>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-emerald-400">Home</Link>
            <Link to="/pricing" className="hover:text-emerald-400">Pricing</Link>
            <Link to="/register" className="hover:text-emerald-400">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoLandingPage;
